import { useEffect, useMemo } from 'react'

import { launchModal, sortArrayBy } from '@domain-group/javascript-utils'

import { useForm } from '@domain-group/react-hooks'

import autoSelectProductOptions from '@functions/autoSelectProductOptions'
import autoSelectProductQuantityAndOptions from '@functions/autoSelectProductQuantityAndOptions'
import filterPriceListItemsByPriceListAndProduct from '@functions/filterPriceListItemsByPriceListAndProduct'
import findCurrentAgencyPriceList from '@functions/findCurrentAgencyPriceList'
import selectPriceListItemOptionValue, { SelectedOption } from '@functions/selectPriceListItemOptionValue'

import useAgencyPrice from '@hooks/useAgencyPrice'
import useProviderPriceLists from '@hooks/useProviderPriceLists'
import useSelector from '@hooks/useSelector'

import type { ProviderPriceListItemOptionModel, ProviderPriceListModel, ServiceProductModel } from '@models/types'
import { EntitiesState } from '@redux/entities'

// @ts-expect-error TS2724
import type { AgencyMarketingPayload } from '@hooks/useAgencyMarketing'

type SelectProviderPriceListItemIdParams = {
  id: number,
  providerPriceListItems: EntitiesState['providerPriceListItems'],
  providerPriceListItemOptionValues: EntitiesState['providerPriceListItemOptionValues'],
  providerPriceListItemOptions: EntitiesState['providerPriceListItemOptions'],
  setEntityState: (arg: object) => void,
}

const selectProviderPriceListItemId = (params: SelectProviderPriceListItemIdParams) => {
  const {
    id,
    providerPriceListItems,
    providerPriceListItemOptionValues,
    providerPriceListItemOptions,
    setEntityState,
  } = params

  const providerPriceListItem = providerPriceListItems[id]

  const autoSelectPayload = autoSelectProductOptions({
    providerPriceListItem,
    providerPriceListItemOptions,
    providerPriceListItemOptionValues,
  })

  setEntityState({
    selectedProviderPriceListItemId: id,
    selectedProviderPriceListItemOptions: autoSelectPayload.selectedProviderPriceListItemOptions,
  })
}

type SelectProviderPriceListItemOptionValueParams = {
  priceListItemOptionParams: {
    providerPriceListItemOption: ProviderPriceListItemOptionModel,
    providerPriceListItemOptionValueId: number,
  },
  providerPriceListItemOptionValues: EntitiesState['providerPriceListItemOptionValues'],
  selectedProviderPriceListItemOptions: SelectedOption[],
  setEntityState: (arg: object) => void,
}

const selectProviderPriceListItemOptionValue = (params: SelectProviderPriceListItemOptionValueParams) => {
  const {
    priceListItemOptionParams: {
      providerPriceListItemOption,
      providerPriceListItemOptionValueId,
    },
    providerPriceListItemOptionValues,
    selectedProviderPriceListItemOptions,
    setEntityState,
  } = params

  const selectPayload = selectPriceListItemOptionValue({
    providerPriceListItemOption,
    providerPriceListItemOptionValues,
    selectedProviderPriceListItemOptions,
    selectedProviderPriceListItemOptionValueId: providerPriceListItemOptionValueId,
  })

  setEntityState({
    selectedProviderPriceListItemOptions: selectPayload.selectedProviderPriceListItemOptions,
  })
}

type AutoSelectQtyOptionsParams = {
  selectedProduct: ServiceProductModel,
  currentPriceList: Partial<ProviderPriceListModel>,
  entities: EntitiesState,
  setEntityState: (arg: object) => void,
}

const autoSelectQtyOptions = (params: AutoSelectQtyOptionsParams) => {
  const {
    selectedProduct,
    currentPriceList,
    entities,
    setEntityState,
  } = params

  const {
    providerPriceListItemOptions,
    providerPriceListItemOptionValues,
    providerPriceListItems,
  } = entities

  const { firstPriceListItem, selectedProviderPriceListItemOptions } = autoSelectProductQuantityAndOptions({
    providerPriceList: currentPriceList,
    providerPriceListItemOptions,
    providerPriceListItemOptionValues,
    providerPriceListItems,
    serviceProduct: selectedProduct,
  })

  setEntityState({
    selectedProviderPriceListItemId: firstPriceListItem ? firstPriceListItem.id : null,
    selectedProviderPriceListItemOptions,
  })
}

const defaultFormState = {
  selectedAgencyId: '',
  selectedProviderPriceListItemOptions: [],
  selectedProviderId: null,
  selectedProviderPriceListItemId: null,
}

export function useAgencyMarketingOrderForm() {
  const agencyMarketingOrderForm = useForm(
    defaultFormState,
    {
      requiredFields: [
        { key: 'selectedAgencyId' },
        { key: 'selectedProviderId' },
        { key: 'selectedProviderPriceListItemId' },
      ],
    },
    [],
  )

  return {
    ...agencyMarketingOrderForm,
  }
}

export type UseAgencyMarketingOrderFormPayload = ReturnType<typeof useAgencyMarketingOrderForm>

type useAgencyMarketingOrderParams = {
  serviceProductId: number,
}

const useAgencyMarketingOrder = (params: useAgencyMarketingOrderParams) => {
  const { serviceProductId } = params

  const agencyMarketingOrderFormPayload = useAgencyMarketingOrderForm()
  const { entityState, setEntityState } = agencyMarketingOrderFormPayload

  const {
    selectedAgencyId,
    selectedProviderId,
    selectedProviderPriceListItemId,
    selectedProviderPriceListItemOptions,
  } = entityState

  // Entities
  const entities = useSelector(reduxState => reduxState.entities)

  const {
    agencyProviders,
    providerPriceListItemOptions,
    providerPriceListItemOptionValues,
    providerPriceListItems,
    providerPriceLists,
    serviceProducts,
  } = entities

  const selectedProduct = serviceProducts[serviceProductId] || {}
  const selectedPriceListItem = providerPriceListItems[selectedProviderPriceListItemId] || {}

  // Current Price List
  const currentPriceList = findCurrentAgencyPriceList({
    agencyId: selectedAgencyId,
    agencyProviders,
    providerId: selectedProviderId,
    providerPriceLists,
    serviceId: selectedProduct.service_id,
  })

  // Autoselect Product Qty & Options
  useEffect(() => {
    // @ts-expect-error TS2339
    if (selectedProduct.id && currentPriceList.id){
      autoSelectQtyOptions({
        selectedProduct,
        currentPriceList,
        entities,
        setEntityState,
      })
    }
  // @ts-expect-error TS2339
  }, [currentPriceList.id, selectedProduct.id])

  // Load Agency Price
  const { currentPrice } = useAgencyPrice({
    providerPriceListItem: selectedPriceListItem,
    selectedProviderPriceListItemOptions,
  })

  // Load Provider Price Lists
  const { loading: loadingPriceLists } = useProviderPriceLists({
    filters: {
      agency_id: selectedAgencyId,
      provider_id: selectedProviderId,
      service_id: selectedProduct.service_id,
    },
    requestOptions: {
      include_provider_price_list_item_option_values: true,
      include_provider_price_list_item_options: true,
      include_provider_price_list_items: true,
      agency_price_list_only: true,
    },
    performHttpRequests: !!selectedAgencyId && !!selectedProduct.service_id && !!selectedProviderId,
  })

  // Price List Items
  const filteredPriceListItems = useMemo(() => {
    const filtered = filterPriceListItemsByPriceListAndProduct({
      providerPriceList: currentPriceList,
      providerPriceListItems,
      serviceProduct: selectedProduct,

    })
    return sortArrayBy(Object.values(filtered), 'asc', 'quantity')
  // @ts-expect-error TS2339
  }, [currentPriceList.id, serviceProductId])

  return {
    agencyMarketingOrderFormPayload,
    callbacks: {
      createOrAddToAgencyMarketingCampaign: ((campaignPayload: {
        agencyMarketingPayload: AgencyMarketingPayload,
      }) => launchModal({
        // @ts-expect-error TS18004
        callbacks,
        modalKey: 'CreateAgencyMarketingCampaignModal',
        payload: { campaignPayload },
      })),
      selectProviderPriceListItemId: (id: number) => {
        selectProviderPriceListItemId({
          id,
          providerPriceListItemOptions,
          providerPriceListItemOptionValues,
          providerPriceListItems,
          setEntityState,
        })
      },
      selectProviderPriceListItemOptionValue: (
        priceListItemOptionParams: {
          providerPriceListItemOption: ProviderPriceListItemOptionModel,
          providerPriceListItemOptionValueId: number,
        },
      ) => {
        selectProviderPriceListItemOptionValue({
          priceListItemOptionParams,
          providerPriceListItemOptionValues,
          selectedProviderPriceListItemOptions,
          setEntityState,
        })
      },
    },
    currentPrice,
    currentPriceList,
    filteredPriceListItems,
    loadingPriceLists,
    selectedProduct,
    selectedProviderPriceListItemId,
    selectedProviderPriceListItemOptions,
  }
}

export type UseAgencyMarketingOrderPayload = ReturnType<typeof useAgencyMarketingOrder>

export default useAgencyMarketingOrder
